import { combineReducers, configureStore } from '@reduxjs/toolkit';
import type { Action } from 'redux';
import {
    FLUSH,
    PAUSE,
    PERSIST,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE,
} from 'redux-persist';
import persistReducer from 'redux-persist/es/persistReducer';
import storage from 'redux-persist/lib/storage';

import { baseApi, vivaApi, carrickApi } from '@services';
import { logout as authLogout } from './actions';
import { RootState } from './types';

import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';

//~ Import your reducers here
import { accountSelectionSlice } from '@features/accounts';
import { appSlice } from '@features/app';
import { authenticationSlice } from '@features/authentication';
import { productionSelectionSlice } from '@features/productions';
import { themeSlice } from '@features/theme';
import { collectionSlice } from '@features/Collection';
import { clipSlice } from '@features/Clip';

// import { advancedSearchSlice } from '@features/advancedSearch';

//~ Reducer combining all 'App related' slices
const appReducers = combineReducers({
    [appSlice.name]: appSlice.reducer,
    [themeSlice.name]: themeSlice.reducer,
});

// Persist Config to persist the theme
const appPersistConfig = {
    key: appSlice.name,
    storage,
    whitelist: ['theme'],
};

const persistedAppReducer = persistReducer(appPersistConfig, appReducers);

const collectionPersistConfig = {
    key: collectionSlice.name,
    storage,
    whitelist: ['selectedCollectionId'],
    stateReconciler: autoMergeLevel1,
};

const clipPersistConfig = {
    key: clipSlice.name,
    storage,
    whitelist: ['selectedClipId'],
    stateReconciler: autoMergeLevel1,
};

const persistedCollectionReducer = persistReducer<
    ReturnType<typeof collectionSlice.getInitialState>
>(collectionPersistConfig, collectionSlice.reducer);

const persistedClipReducder = persistReducer<
    ReturnType<typeof clipSlice.getInitialState>
>(clipPersistConfig, clipSlice.reducer);

//~ Base root reducer combining all slices
export const rootReducer = combineReducers({
    app: persistedAppReducer,

    [authenticationSlice.name]: authenticationSlice.reducer,
    [accountSelectionSlice.name]: accountSelectionSlice.reducer,
    [productionSelectionSlice.name]: productionSelectionSlice.reducer,
    // [clipSlice.name]: clipSlice.reducer,
    // [collectionSlice.name]: collectionSlice.reducer,
    [clipSlice.name]: persistedClipReducder,
    [collectionSlice.name]: persistedCollectionReducer,
    // [advancedSearchSlice.name]: advancedSearchSlice.reducer,

    [baseApi.reducerPath]: baseApi.reducer,
    [vivaApi.reducerPath]: vivaApi.reducer,
    [carrickApi.reducerPath]: carrickApi.reducer,
});

//~ Global meta-reducer
const resettableReducer = (state: RootState | undefined, action: Action) => {
    //~ Checking for specific 'logout' action
    if (action.type === authLogout.type) {
        //~ Specify which top-level keys you want to reset here
        const { app /*, ...resetStates */ } = state || {};
        //~ Destructuring to keep 'app' untouched

        return rootReducer(
            {
                app,
                //~ All other keys implicitly passed as undefined will be reset by their reducers
            } as RootState,
            action,
        );

        //~ Alternatively, to resset EVERYTHING:
        // return appReducers(undefined, action);
    }

    //~ For all other actions, just run the normal reducers
    return rootReducer(state, action);
};

//~ Persistence configuration for nested slices
const rootPersistConfig = {
    key: 'root',
    storage,
    whitelist: [
        authenticationSlice.name,
        accountSelectionSlice.name,
        productionSelectionSlice.name,
    ],
};

const persistedReducer = persistReducer(rootPersistConfig, resettableReducer);

export const store = configureStore({
    reducer: persistedReducer,

    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                    REHYDRATE,
                    authLogout.type,
                ],
            },
        }).concat(
            baseApi.middleware,
            vivaApi.middleware,
            carrickApi.middleware,
        ),
});

export const persistor = persistStore(store);
export default store;
